import LocalScene, {
  GeocentricCoordinate,
  ProjectedCoordinate,
} from '../projections';
import {
  AerialAreaMeasurement,
  AerialMeasurement,
  Measurement3D,
  MeasurementArea,
  MeasurementPoint,
  MeasurementVolume,
  MeasurementController, StationMeasurement, MeasurementGroup
} from '.';
import { Vector3 } from 'three';
import { windowSafeDateISO } from '../utilities';
import {
  measureCSVFilter,
  measureDataFilter,
  measureDataFormat
} from '../../../file-extensions';
import { t } from '../../../localization';
import { writeToString } from "fast-csv";
import { downloadFile } from '../../../utilities';

export class MeasurementGroupExporter {
  private controller: MeasurementController;

  constructor(controller) {
    this.controller = controller;
  }

  get viewer() {
    return this.controller.viewer;
  }

  get formatter() {
    return this.controller.formatter;
  }

  exportLinework(measurementGroup, type, version) {
    //let lines = measurementGroup.getLineCoordinates();
    //let points = measurementGroup.getPointCoordinates();
    //let name = `${measurementGroup.name} (${type}).zip`;

    // Need to implement dxf/shp javascript writer
  }

  async exportCSV(measurementGroup, ordering) {
    await this.handleExport3D(measurementGroup, ordering);
    await this.handleExport2D(measurementGroup, ordering);
  }

  async exportDAT(measurementGroup) {
    const name = `${this.viewer.projectName}.${measureDataFormat}`;
    const data = this.getDataExport(measurementGroup);
    const content = JSON.stringify(data, null, 2);
    downloadFile(name, content, "plain", measureDataFilter);
  }

  async handleExport2D(measurementGroup: MeasurementGroup, ordering) {
    const name = t('measure.name-measurements-2d', {
      name: this.viewer.projectName,
      date: windowSafeDateISO()
    });

    const rows = this.getExportRows2D(measurementGroup, ordering);
    if (rows.length === 0) return;

    const content = await writeToString(rows);
    downloadFile(name, content, "csv", measureCSVFilter);
  }

  async handleExport3D(measurementGroup: MeasurementGroup, ordering) {
    const name = t('measure.name-measurements-3d', {
      name: this.viewer.projectName,
      date: windowSafeDateISO()
    });

    const rows = this.getExportRows3D(measurementGroup, ordering);
    if (rows.length === 0) return;

    const content = await writeToString(rows);
    downloadFile(name, content, "csv", measureCSVFilter);
  }

  /** @private */
  getDataExport(measurementGroup: MeasurementGroup) {
    let data = {
      count: 0,
      name: measurementGroup.name,
      projection: LocalScene.dataProjection,
      measurements: {
        measurements2D: [],
        measurements3D: []
      }
    };

    let measurements = measurementGroup.measurements;
    measurements.forEach(measurement => {
      const created = measurement.timeCreated
        .toISOString()
        .split('.')[0];

      const info = {
        id: null,
        coordinates: measurement.coordinates,
        time_created: created,
        title: measurement.title,
        type: measurement.type,
        volume: null,
        station: null,
        along: null,
        across: null,
        units: null
      };

      if (measurement instanceof AerialMeasurement) {
        data.measurements.measurements2D.push(info);
      } else {
        if (measurement instanceof MeasurementVolume) {
          info.volume = measurement.volumeHelper.value;
        }
        if (measurement instanceof StationMeasurement) {
          info.station = measurement.stationStart;
          info.across = measurement.distanceAcross;
          info.along = measurement.distanceAlong;
          info.units = measurement.units;
        }

        data.measurements.measurements3D.push(info);
      }

      data.count += 1;
    });

    return data;
  }

  /** @private */
  getExportRows2D(measurementGroup, ordering) {
    let measureCsvOut = [];

    const precision = this.formatter
      .getVectorPrecision(LocalScene.viewProjection);

    let measurements = measurementGroup.measurements.filter(measurement =>
      measurement instanceof AerialMeasurement
    );

    if (measurements.length === 0) {
      return measureCsvOut;
    }

    let mapProjection = this.viewer.minimap.mapSystem;

    let header = this.getHeaderValues2D(measurements, ordering);
    let nullValue = this.formatter.nullValue;
    measureCsvOut.push(header);

    // Load measurements in reverse order
    measurements.reverse();
    measurements.forEach(measurement => {
      let row;
      let value;

      if (measurement instanceof AerialAreaMeasurement) {
        value = this.formatter.convertUnits(
          measurement.area2d, measurement.type);
        row = [measurement.title, t('measure.area'), nullValue, value];
      } else {
        value = this.formatter.convertUnits(
          measurement.distance2D, measurement.type);
        row = [measurement.title, t('measure.length'), value, nullValue];
      }

      let coordinates = measurement.geometry.flatCoordinates;
      if (measurement instanceof AerialAreaMeasurement) {
        coordinates = coordinates.slice(0, coordinates.length - 2);
      }

      let vertices = [];
      let numPoints = coordinates.length / 2.0;

      for (var i = 0; i < numPoints; i++) {
        let x = coordinates[2 * i];
        let y = coordinates[(2 * i) + 1];

        let coord = new Vector3(x, y, 0.0);
        let ecef = new ProjectedCoordinate(coord, mapProjection).toGeocentric();
        let proj = new GeocentricCoordinate(ecef).toViewProjection();
        vertices.push(proj);
      }

      vertices.forEach(vertex => {
        // Export with x and y values swapped
        if (ordering === "yxz") {
          vertex = new Vector3(vertex.y, vertex.x, vertex.z);
        }

        row = row.concat([
          vertex.x.toFixed(precision.x),
          vertex.y.toFixed(precision.y),
        ]);
      });

      measureCsvOut.push(row);
    });

    return measureCsvOut;
  }

  /** @private */
  getExportRows3D(measurementGroup, ordering) {
    let measureCsvOut = [];

    const precision = this.formatter
      .getVectorPrecision(LocalScene.viewProjection);

    let measurements = measurementGroup.measurements.filter(measurement =>
      measurement instanceof Measurement3D
    );

    if (measurements.length === 0) {
      return measureCsvOut;
    }

    let header = this.getHeaderValues3D(measurements, ordering);
    let nullValue = this.formatter.nullValue;
    measureCsvOut.push(header);

    // Load measurements in reverse order
    measurements.reverse();
    measurements.forEach(measurement => {
      let row;

      if (measurement instanceof MeasurementPoint) {
        // Most data will be blank for a single point
        row = [
          measurement.title,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue
        ];
      } else if (measurement instanceof StationMeasurement){
        row = [
          measurement.title,
          this.formatter.convertUnits(
            measurement.distance2D, measurement.type),
          this.formatter.convertUnits(
            measurement.distance3D, measurement.type),
          nullValue,
          nullValue,
          this.formatter.formatGrade(measurement.grade, false),
          this.formatter.formatAngle(measurement.angle, false),
          this.formatter.formatStation(measurement, false),
          this.formatter.formatOffset(measurement, false)
        ];
      } else if (measurement instanceof MeasurementArea) {
        let areaValue;
        let volumeValue = nullValue;

        if (measurement.valid) {
          areaValue = this.formatter.convertUnits(
            measurement.area3D, measurement.type);
        } else {
          areaValue = measurement.areaInvalidMessage;
        }

        if (measurement instanceof MeasurementVolume) {
          volumeValue = measurement.invalidVolumeMessage;
          if (measurement.valid && measurement.calculated) {
            volumeValue = this.formatter.convertUnits(
              measurement.volume, measurement.type);
          }
        }

        row = [
          measurement.title,
          nullValue,
          nullValue,
          areaValue,
          volumeValue,
          nullValue,
          nullValue,
          nullValue,
          nullValue
        ];
      } else {
        row = [
          measurement.title,
          this.formatter.convertUnits(
            measurement.distance2D, measurement.type),
          this.formatter.convertUnits(
            measurement.distance3D, measurement.type),
          nullValue,
          nullValue,
          this.formatter.formatGrade(measurement.grade, false),
          this.formatter.formatAngle(measurement.angle, false),
          nullValue,
          nullValue
        ];
      }

      let verticesReprojected = measurement.vertices.map(vertex => {
        return vertex.toViewProjection();
      });

      verticesReprojected.forEach(vertex => {
        // Export with x and y values swapped
        if (ordering === "yxz") {
          vertex = new Vector3(vertex.y, vertex.x, vertex.z);
        }

        row = row.concat([
          vertex.x.toFixed(precision.x),
          vertex.y.toFixed(precision.y),
          vertex.z.toFixed(precision.z)
        ]);
      });

      measureCsvOut.push(row);
    });

    return measureCsvOut;
  }

  getHeaderValues2D(measurements, ordering) {
    let header = [
      t('measure.notes'),
      t('measure.measure-type'),
      t('measure.length-formatter-units', {
        units: this.formatter.units
      }),
      t('measure.area-formatter-units-2', {
        units: this.formatter.units
      })
    ];

    let numVertices = 0;
    measurements.forEach(measurement => {
      let coordinates = measurement.geometry.flatCoordinates;
      if (measurement instanceof AerialAreaMeasurement) {
        coordinates = coordinates.slice(0, coordinates.length - 2);
      }

      let numPoints = coordinates.length / 2.0;
      numVertices = Math.max(numVertices, numPoints);
    });

    for (var i = 0; i < numVertices; i++) {
      if (ordering === "yxz") {
        header = header.concat([`Y_${i + 1}`, `X_${i + 1}`]);
      } else {
        header = header.concat([`X_${i + 1}`, `Y_${i + 1}`]);
      }
    }

    return header;
  }

  getHeaderValues3D(measurements, ordering) {
    let header = [
      t('measure.notes'),
      t('measure.distance-2d-formatter-units', {
        units: this.formatter.units
      }),
      t('measure.distance-3d-formatter-units', {
        units: this.formatter.units
      }),
      t('measure.area-3d-formatter-areaunits', {
        units: this.formatter.areaUnits
      }),
      t('measure.volume-3d-formatter-volumeunits', {
        units: this.formatter.volumeUnits
      }),
      t('measure.grade'),
      t('measure.angle-deg'),
      t('measure.station-info'),
      t('measure.station-offset')
    ];

    // Determine the max number of vertices
    let numVertices = 0;
    measurements.forEach(measurement => {
      let measurementVertices = measurement.vertices.length;
      numVertices = Math.max(numVertices, measurementVertices);
    });

    for (var i = 0; i < numVertices; i++) {
      if (ordering === "yxz") {
        header = header.concat([`Y_${i + 1}`, `X_${i + 1}`, `Z_${i + 1}`]);
      } else {
        header = header.concat([`X_${i + 1}`, `Y_${i + 1}`, `Z_${i + 1}`]);
      }
    }

    return header;
  }
}
